import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, Filter, Flame, ExternalLink, UserCog, Sparkles, Phone, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { adminApi, PIPELINE_STAGES } from "@/lib/adminApi";
import { useAuth } from "@/lib/auth";
import { useDialer } from "@/components/dialer/DialerProvider";
import StatusPill from "@/components/admin/StatusPill";
import LeadDrawer from "@/components/admin/LeadDrawer";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";
import ArchivedFilterToggle, { ArchivedRowBadge } from "@/components/ArchivedFilterToggle";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const SOURCES = ["homepage_form", "jarvis", "referral"];
const PRODUCTS = ["Term", "Whole Life", "IUL", "Final Expense", "Annuities", "Health", "Medicare", "Dental/Vision"];

export default function AdminLeads() {
    const [params, setParams] = useSearchParams();
    const { user } = useAuth();
    const isAdminOrMaster = ["master_admin", "admin"].includes(user?.role);

    const [filters, setFilters] = useState({
        status: params.get("status") || "",
        source: params.get("source") || "",
        product: params.get("product") || "",
        hot: params.get("hot") || "",
        assigned_to: params.get("assigned_to") || "",
        q: params.get("q") || "",
    });
    const [page, setPage] = useState(Number(params.get("page") || 1));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeId, setActiveId] = useState(params.get("open") || null);
    const [agents, setAgents] = useState([]);
    const [unassignedCount, setUnassignedCount] = useState(0);
    const [distributing, setDistributing] = useState(false);
    // Phase 11 D — soft-delete archive filter + confirmation modal
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);  // {id, name} | null
    const dialer = useDialer();
    const callLead = (l, e) => {
        e?.stopPropagation?.();
        try { dialer?.startCall?.({ leadId: l.id, phone: l.phone, name: l.name }); } catch { /* noop */ }
    };

    const load = async () => {
        setLoading(true);
        try {
            const q = {
                page,
                page_size: 25,
                sort: "-created_at",
                ...(includeDeleted ? { include_deleted: true } : {}),
                ...(filters.status ? { status: filters.status } : {}),
                ...(filters.source ? { source: filters.source } : {}),
                ...(filters.product ? { product: filters.product } : {}),
                ...(filters.hot ? { hot: filters.hot === "true" } : {}),
                ...(filters.assigned_to ? { assigned_to: filters.assigned_to } : {}),
                ...(filters.q ? { q: filters.q } : {}),
            };
            const res = await adminApi.leads(q);
            setData(res);
        } catch {
            toast.error("Couldn't load leads.");
        } finally {
            setLoading(false);
        }
    };

    const refreshUnassignedCount = async () => {
        try {
            const r = await adminApi.leads({ assigned_to: "unassigned", page_size: 1 });
            setUnassignedCount(r.total || 0);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        load();
         
    }, [filters, page, includeDeleted]);

    useEffect(() => {
        if (!isAdminOrMaster) return;
        adminApi
            .teamList({ role: "agent", status: "active", page_size: 100 })
            .then((r) => setAgents(r.items || []))
            .catch(() => {});
        refreshUnassignedCount();
         
    }, [isAdminOrMaster]);

    useEffect(() => {
        const next = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => v && next.set(k, v));
        if (page !== 1) next.set("page", page);
        if (activeId) next.set("open", activeId);
        setParams(next, { replace: true });
         
    }, [filters, page, activeId]);

    const handleDistribute = async () => {
        setDistributing(true);
        try {
            const r = await adminApi.distributeUnassigned();
            toast.success(
                `Assigned ${r.assigned_count} leads. ${r.still_unassigned_count} still unassigned (no eligible agents).`,
            );
            await load();
            await refreshUnassignedCount();
        } catch {
            toast.error("Couldn't distribute unassigned leads.");
        } finally {
            setDistributing(false);
        }
    };

    const agentLookup = React.useMemo(() => {
        const map = new Map();
        for (const a of agents) map.set(a.id, a);
        return map;
    }, [agents]);

    return (
        <div className="space-y-5" data-testid="admin-leads">
            <header className="flex flex-wrap items-start justify-between gap-3 sm:items-center">
                <div>
                    <div className="mm-eyebrow">Leads</div>
                    <h1 className="mm-display text-2xl text-mm-navy sm:text-3xl">Pipeline</h1>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    {isAdminOrMaster && (
                        <ArchivedFilterToggle
                            value={includeDeleted}
                            onChange={(v) => { setPage(1); setIncludeDeleted(v); }}
                            testid="leads-archived-toggle"
                        />
                    )}
                    {isAdminOrMaster && unassignedCount > 0 && (
                        <button
                            onClick={handleDistribute}
                            disabled={distributing}
                            className="mm-btn-outline !py-2 text-xs disabled:opacity-60"
                            data-testid="leads-distribute-unassigned"
                        >
                            {distributing ? (
                                <><Loader2 size={12} className="animate-spin" /> Distributing…</>
                            ) : (
                                <><Sparkles size={12} /> Distribute {unassignedCount} unassigned</>
                            )}
                        </button>
                    )}
                    <div className="flex items-center gap-2 text-sm text-mm-muted">
                        <Filter size={14} />
                        Showing {data?.items?.length || 0} of {data?.total ?? 0}
                    </div>
                </div>
            </header>

            {/* Filters */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-7">
                <Input
                    placeholder="Search name, email, phone…"
                    value={filters.q}
                    onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, q: e.target.value })); }}
                    className="lg:col-span-2"
                    data-testid="leads-filter-q"
                />
                <SelectFilter
                    value={filters.status}
                    onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, status: v })); }}
                    options={PIPELINE_STAGES}
                    placeholder="All statuses"
                    testId="leads-filter-status"
                />
                <SelectFilter
                    value={filters.source}
                    onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, source: v })); }}
                    options={SOURCES}
                    placeholder="All sources"
                    testId="leads-filter-source"
                />
                <SelectFilter
                    value={filters.product}
                    onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, product: v })); }}
                    options={PRODUCTS}
                    placeholder="All products"
                    testId="leads-filter-product"
                />
                <SelectFilter
                    value={filters.hot}
                    onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, hot: v })); }}
                    options={["true", "false"]}
                    placeholder="Hot?"
                    testId="leads-filter-hot"
                />
                {isAdminOrMaster && (
                    <Select
                        value={filters.assigned_to || "__all__"}
                        onValueChange={(v) => {
                            setPage(1);
                            setFilters((f) => ({ ...f, assigned_to: v === "__all__" ? "" : v }));
                        }}
                    >
                        <SelectTrigger data-testid="leads-filter-assigned-to">
                            <SelectValue placeholder="Assigned To" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All assignees</SelectItem>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {agents.map((a) => (
                                <SelectItem key={a.id} value={a.id}>
                                    {[a.first_name, a.last_name].filter(Boolean).join(" ") || a.email}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {/* Table / cards */}
            <div className="mm-card overflow-hidden" data-testid="leads-table">
                {/* Desktop / tablet: table */}
                <div className="hidden overflow-x-auto sm:block">
                    <table className="w-full text-sm">
                        <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                            <tr>
                                <Th>Name</Th>
                                <Th>Contact</Th>
                                <Th>Product</Th>
                                <Th>Budget</Th>
                                <Th>Score</Th>
                                <Th>Status</Th>
                                <Th>Assigned to</Th>
                                <Th>Source</Th>
                                <Th>Age</Th>
                                <Th>Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={10} className="py-10 text-center">
                                    <Loader2 className="mx-auto animate-spin text-mm-navy" />
                                </td></tr>
                            ) : data?.items?.length === 0 ? (
                                <tr><td colSpan={10} className="py-10 text-center text-mm-muted">No leads match your filters.</td></tr>
                            ) : (
                                data?.items?.map((l) => {
                                    const archived = !!l.deleted_at;
                                    return (
                                    <tr
                                        key={l.id}
                                        onClick={() => setActiveId(l.id)}
                                        className={`cursor-pointer border-t border-mm-line transition-colors hover:bg-mm-bg ${archived ? "opacity-60" : ""}`}
                                        data-testid={`leads-row-${l.id}`}
                                    >
                                        <Td>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-mm-ink">{l.name || "—"}</span>
                                                {l.hot_flag && <Flame size={12} className="text-rose-600" />}
                                                {archived && <ArchivedRowBadge deletedAt={l.deleted_at} testid={`leads-archived-badge-${l.id}`} />}
                                            </div>
                                        </Td>
                                        <Td>
                                            <div className="text-mm-ink">{l.email || "—"}</div>
                                            <div className="text-xs text-mm-muted">{l.phone || ""}</div>
                                        </Td>
                                        <Td>{l.product_interest || "—"}</Td>
                                        <Td>{l.budget || "—"}</Td>
                                        <Td className="tabular-nums">{l.score ?? "—"}</Td>
                                        <Td><StatusPill status={l.status} /></Td>
                                        <Td><AssigneePill lead={l} agentLookup={agentLookup} /></Td>
                                        <Td className="text-mm-muted">{l.source}</Td>
                                        <Td className="text-mm-muted">{relativeTime(l.created_at)}</Td>
                                        <Td>
                                            <div className="flex items-center gap-1">
                                                {l.phone && !archived && (
                                                    <button
                                                        onClick={(e) => callLead(l, e)}
                                                        className="rounded-full p-1.5 text-emerald-700 hover:bg-emerald-50"
                                                        title={`Call ${l.phone}`}
                                                        data-testid={`leads-call-${l.id}`}
                                                    >
                                                        <Phone size={12} />
                                                    </button>
                                                )}
                                                {l.hot_flag && !archived ? (
                                                    <Link
                                                        to={`/admin/leads/${l.id}/vip`}
                                                        className="mm-btn-ghost text-xs"
                                                        onClick={(e) => e.stopPropagation()}
                                                        data-testid={`leads-vip-${l.id}`}
                                                    >
                                                        VIP <ExternalLink size={11} />
                                                    </Link>
                                                ) : null}
                                                {isAdminOrMaster && !archived && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setPendingDelete({ id: l.id, name: l.name || l.email || l.phone || l.id.slice(0, 8) }); }}
                                                        className="rounded-full p-1.5 text-red-600 hover:bg-red-50"
                                                        title="Archive this lead"
                                                        data-testid={`leads-delete-${l.id}`}
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                )}
                                                {isAdminOrMaster && archived && (
                                                    <button
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                await adminApi.restore("leads", l.id);
                                                                toast.success("Lead restored.");
                                                                load();
                                                            } catch {
                                                                toast.error("Couldn't restore.");
                                                            }
                                                        }}
                                                        className="rounded-full p-1.5 text-emerald-700 hover:bg-emerald-50"
                                                        title="Restore this lead"
                                                        data-testid={`leads-restore-${l.id}`}
                                                    >
                                                        <RotateCcw size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </Td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile: stacked cards (<sm) */}
                <div className="divide-y divide-mm-line sm:hidden">
                    {loading ? (
                        <div className="py-10 text-center">
                            <Loader2 className="mx-auto animate-spin text-mm-navy" />
                        </div>
                    ) : data?.items?.length === 0 ? (
                        <div className="py-10 text-center text-sm text-mm-muted">
                            No leads match your filters.
                        </div>
                    ) : (
                        data?.items?.map((l) => {
                            const archived = !!l.deleted_at;
                            return (
                                <button
                                    key={l.id}
                                    type="button"
                                    onClick={() => setActiveId(l.id)}
                                    className={`block w-full px-4 py-4 text-left hover:bg-mm-bg ${archived ? "opacity-60" : ""}`}
                                    data-testid={`leads-row-${l.id}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                <span className="truncate text-base font-semibold text-mm-ink">
                                                    {l.name || "—"}
                                                </span>
                                                {l.hot_flag && <Flame size={14} className="text-rose-600" />}
                                                {archived && (
                                                    <ArchivedRowBadge
                                                        deletedAt={l.deleted_at}
                                                        testid={`leads-archived-badge-${l.id}`}
                                                    />
                                                )}
                                            </div>
                                            <div className="mt-1 truncate text-sm text-mm-ink">
                                                {l.email || "—"}
                                            </div>
                                            {l.phone && (
                                                <div className="text-sm text-mm-muted">{l.phone}</div>
                                            )}
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <div className="tabular-nums text-sm font-semibold text-mm-navy">
                                                {l.score ?? "—"}
                                            </div>
                                            <div className="mt-0.5 text-[10px] uppercase tracking-wider text-mm-muted">
                                                score
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <StatusPill status={l.status} />
                                        <AssigneePill lead={l} agentLookup={agentLookup} />
                                        <span className="text-[11px] text-mm-muted">
                                            {l.product_interest || "—"}
                                        </span>
                                        <span className="text-[11px] text-mm-muted">·</span>
                                        <span className="text-[11px] text-mm-muted">
                                            {relativeTime(l.created_at)}
                                        </span>
                                    </div>

                                    <div
                                        className="mt-3 flex items-center gap-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {l.phone && !archived && (
                                            <button
                                                onClick={(e) => callLead(l, e)}
                                                className="inline-flex h-10 min-w-[44px] items-center justify-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 text-xs font-medium text-emerald-700"
                                                data-testid={`leads-call-${l.id}`}
                                            >
                                                <Phone size={13} /> Call
                                            </button>
                                        )}
                                        {l.hot_flag && !archived && (
                                            <Link
                                                to={`/admin/leads/${l.id}/vip`}
                                                className="inline-flex h-10 items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-3 text-xs font-medium text-rose-700"
                                                onClick={(e) => e.stopPropagation()}
                                                data-testid={`leads-vip-${l.id}`}
                                            >
                                                VIP <ExternalLink size={11} />
                                            </Link>
                                        )}
                                        {isAdminOrMaster && !archived && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPendingDelete({
                                                        id: l.id,
                                                        name: l.name || l.email || l.phone || l.id.slice(0, 8),
                                                    });
                                                }}
                                                className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-full border border-red-100 text-red-600"
                                                title="Archive this lead"
                                                data-testid={`leads-delete-${l.id}`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                        {isAdminOrMaster && archived && (
                                            <button
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    try {
                                                        await adminApi.restore("leads", l.id);
                                                        toast.success("Lead restored.");
                                                        load();
                                                    } catch {
                                                        toast.error("Couldn't restore.");
                                                    }
                                                }}
                                                className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-full border border-emerald-100 text-emerald-700"
                                                title="Restore this lead"
                                                data-testid={`leads-restore-${l.id}`}
                                            >
                                                <RotateCcw size={14} />
                                            </button>
                                        )}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
                {data && data.total_pages > 1 && (
                    <div className="flex items-center justify-between border-t border-mm-line px-4 py-2 text-sm">
                        <div className="text-mm-muted">
                            Page {data.page} of {data.total_pages}
                        </div>
                        <div className="flex gap-2">
                            <button
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="mm-btn-ghost disabled:opacity-40"
                            >
                                ← Prev
                            </button>
                            <button
                                disabled={page >= data.total_pages}
                                onClick={() => setPage((p) => p + 1)}
                                className="mm-btn-ghost disabled:opacity-40"
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <LeadDrawer
                leadId={activeId}
                open={!!activeId}
                onOpenChange={(o) => !o && setActiveId(null)}
                onChanged={load}
            />

            <ConfirmDeleteModal
                open={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                onConfirm={async (reason) => {
                    try {
                        await adminApi.softDelete("leads", pendingDelete.id, reason);
                        toast.success("Lead archived. Restore within 30 days from the archived filter.");
                        setPendingDelete(null);
                        load();
                    } catch {
                        toast.error("Couldn't archive.");
                    }
                }}
                resourceLabel="lead"
                resourceName={pendingDelete?.name}
                testidPrefix="leads-confirm-delete"
            />
        </div>
    );
}

function Th({ children }) { return <th className="px-3 py-2.5 text-left font-medium">{children}</th>; }
function Td({ children, className = "" }) {
    return <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>;
}

function SelectFilter({ value, onChange, options, placeholder, testId }) {
    return (
        <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid={testId}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="__all__">{placeholder}</SelectItem>
                {options.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
            </SelectContent>
        </Select>
    );
}

function relativeTime(iso) {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
}

function AssigneePill({ lead, agentLookup }) {
    if (!lead.assigned_to_user_id) {
        return (
            <span
                className="inline-flex items-center gap-1 rounded-full border border-mm-navy/40 bg-white px-2 py-0.5 text-[10px] font-medium text-mm-navy"
                data-testid={`assignee-pill-${lead.id}`}
            >
                <UserCog size={10} />
                Unassigned
            </span>
        );
    }
    const a = agentLookup.get(lead.assigned_to_user_id);
    const label = a
        ? `${(a.first_name || "").trim()}${a.last_name ? " " + a.last_name[0] + "." : ""}`.trim() || a.email
        : (lead.assigned_to_email || "Agent");
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-900"
            data-testid={`assignee-pill-${lead.id}`}
            title={lead.assigned_to_email || ""}
        >
            <UserCog size={10} />
            {label}
            {lead.cross_state_assignment && (
                <span
                    className="ml-1 rounded bg-amber-200 px-1 text-[9px] text-amber-900"
                    title="Cross-state assignment — no agent licensed in this state"
                >
                    XS
                </span>
            )}
        </span>
    );
}
